import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Phone, XCircle } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { TicketPass } from "../components/TicketPass";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [data, setData] = useState(null);
    const [pending, setPending] = useState(true);
    const attempts = useRef(0);
    const { t } = useLang();

    useEffect(() => {
        if (!sessionId) { setPending(false); return; }
        const poll = async () => {
            try {
                const { data: status } = await api.get(`/payments/status/${sessionId}`);
                if (status.payment_status === "paid") {
                    const { data: order } = await api.get(`/tickets/order/${sessionId}`);
                    setData(order);
                    setPending(false);
                    return;
                }
            } catch {}
            attempts.current += 1;
            if (attempts.current < 10) setTimeout(poll, 2000);
            else setPending(false);
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div data-testid="payment-success-page" className="pt-36 pb-24 min-h-screen">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
                {pending ? (
                    <div className="flex flex-col items-center gap-5" data-testid="payment-confirming">
                        <div className="w-10 h-10 rounded-full border-2 border-lakou-gold/30 border-t-lakou-gold animate-spin" />
                        <p className="font-display font-bold text-xl text-lakou-cream">{t("pay_confirming")}</p>
                    </div>
                ) : data ? (
                    <>
                        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }}>
                            <CheckCircle2 size={56} className="mx-auto text-lakou-gold" />
                        </motion.div>
                        <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl text-lakou-cream tracking-tight" data-testid="payment-success-title">
                            {data.order_type === "ticket" ? t("pay_in_ticket") : t("pay_in_shop")}
                        </h1>
                        <p className="mt-3 text-lakou-grey">
                            {data.order_type === "ticket" ? t("pay_ticket_sub") : t("pay_shop_sub")}
                        </p>
                        {data.tickets && (
                            <div className="mt-10 space-y-6 text-left">
                                {data.tickets.map((tk) => <TicketPass key={tk.code} ticket={tk} />)}
                            </div>
                        )}
                        {data.order && data.order_type === "ticket" && (
                            <div className="mt-8 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-7 text-left" data-testid="receipt-card">
                                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold font-bold">{t("rc_receipt")}</div>
                                <div className="mt-4 space-y-2.5 font-mono2 text-sm">
                                    <div className="flex justify-between gap-4">
                                        <span className="text-lakou-grey text-xs">{data.order.quantity} × {data.order.category_name}</span>
                                        <span className="text-lakou-cream" data-testid="receipt-subtotal">${(data.order.net_amount ?? (data.amount - (data.order.stripe_fee_expected || 0))).toFixed(2)}</span>
                                    </div>
                                    {(data.order.stripe_fee_expected || 0) > 0 && (
                                        <div className="flex justify-between gap-4">
                                            <span className="text-lakou-grey text-xs">{t("rc_fee")}</span>
                                            <span className="text-lakou-cream" data-testid="receipt-fee">${data.order.stripe_fee_expected.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between gap-4 border-t border-dashed border-lakou-gold/25 pt-3">
                                        <span className="text-lakou-sand uppercase tracking-widest text-xs">{t("rc_total")}</span>
                                        <span className="text-lakou-gold font-bold" data-testid="receipt-total">${data.amount?.toFixed(2)} {data.currency?.toUpperCase()}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <span className="text-lakou-grey text-xs">{t("rc_method")}</span>
                                        <span className="text-lakou-cream" data-testid="receipt-method">{(data.payment_method || "").toUpperCase()}</span>
                                    </div>
                                    {data.transaction_id && (
                                        <div className="flex justify-between gap-4">
                                            <span className="text-lakou-grey text-xs shrink-0">{t("rc_txid")}</span>
                                            <span className="text-lakou-cream text-[11px] break-all text-right" data-testid="receipt-txid">{data.transaction_id}</span>
                                        </div>
                                    )}
                                    {data.confirmation && (
                                        <div className="flex justify-between gap-4">
                                            <span className="text-lakou-grey text-xs shrink-0">{t("rc_conf")}</span>
                                            <span className="text-lakou-gold text-xs tracking-[0.15em]" data-testid="receipt-conf">{data.confirmation}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6 rounded-2xl border border-lakou-gold/40 bg-lakou-raise/60 p-5 text-center" data-testid="receipt-vip">
                                    <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold font-bold">{t("rc_vip_title")}</div>
                                    <p className="mt-2 text-xs text-lakou-grey">{t("rc_vip_sub")}</p>
                                    <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-3">
                                        <a href="tel:+14072279076" data-testid="receipt-vip-call-1"
                                            className="inline-flex items-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs px-5 py-2.5 transition-colors duration-300">
                                            <Phone size={13} /> 407-227-9076
                                        </a>
                                        <a href="tel:32609636" data-testid="receipt-vip-call-2"
                                            className="inline-flex items-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs px-5 py-2.5 transition-colors duration-300">
                                            <Phone size={13} /> 326-096-36
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                        {data.order?.items && (
                            <div className="mt-10 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-7 text-left space-y-3" data-testid="order-summary">
                                <div className="font-display font-extrabold text-lakou-cream">Order summary</div>
                                {data.order.items.map((i, idx) => (
                                    <div key={idx} className="flex justify-between text-sm text-lakou-grey">
                                        <span>{i.qty} × {i.name}{i.size ? ` (${i.size})` : ""}</span>
                                        <span className="font-mono2 text-lakou-gold">${(i.price * i.qty).toFixed(2)}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between font-mono2 text-sm border-t border-lakou-gold/15 pt-3">
                                    <span className="text-lakou-grey uppercase tracking-widest text-xs">{t("total")}</span>
                                    <span className="text-lakou-gold">${data.amount?.toFixed(2)} {data.currency?.toUpperCase()}</span>
                                </div>
                            </div>
                        )}
                        {data.order && data.order_type === "ticket" && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/receipt/${sessionId}.pdf`} target="_blank" rel="noopener noreferrer" data-testid="receipt-pdf"
                                className="mt-6 inline-flex items-center gap-2 rounded-full border border-lakou-gold/40 text-lakou-cream hover:text-lakou-gold font-display font-bold text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors">
                                <Download size={14} /> {t("rc_pdf")}
                            </a>
                        )}
                        <div>
                            <Link to="/" data-testid="payment-back-home"
                                className="mt-6 inline-block rounded-full border border-lakou-gold/40 text-lakou-cream hover:text-lakou-gold font-display font-bold text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors">
                                {t("pay_back")}
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        <XCircle size={56} className="mx-auto text-lakou-orangelight" />
                        <h1 className="mt-6 font-display font-black text-3xl text-lakou-cream" data-testid="payment-unknown-title">{t("pay_fail")}</h1>
                        <p className="mt-3 text-lakou-grey">{t("pay_fail_sub")}</p>
                        <Link to="/contact" data-testid="payment-contact-link"
                            className="mt-8 inline-block rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors">
                            {t("nav_contact")}
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

export function PaymentCancel() {
    const { t } = useLang();
    return (
        <div data-testid="payment-cancel-page" className="pt-36 pb-24 min-h-screen">
            <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
                <XCircle size={56} className="mx-auto text-lakou-orangelight" />
                <h1 className="mt-6 font-display font-black text-3xl text-lakou-cream tracking-tight">{t("cancel_title")}</h1>
                <p className="mt-3 text-lakou-grey">{t("cancel_sub")}</p>
                <div className="mt-8 flex justify-center gap-4">
                    <Link to="/tickets" data-testid="cancel-back-tickets"
                        className="rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors">
                        {t("cancel_tickets")}
                    </Link>
                </div>
            </div>
        </div>
    );
}
